import { useState } from 'react';

import {
  useCreateCloudBackup,
  useFeatureFlag,
  useIsCloudConnected,
  useRestoreState,
  useRouter,
} from '@sbrc/hooks';

import { RestoreConfirmationModal } from './RestoreConfirmationModal';
import { useDropSessionAfterRestore } from './useDropSessionAfterRestore';

/**
 * Whether "Not now"/"Hide" was pressed for the restore now on screen.
 *
 * Keyed on the command rather than held as a flag: the busy condition that
 * makes the button available can stay true from one restore to the next, so
 * a flag would never be cleared and the next command's modal would never
 * appear at all — for a restore nobody snoozed.
 */
export const isSnoozedFor = (
  snoozedCommandId: string | undefined,
  commandId: string | undefined,
): boolean => snoozedCommandId !== undefined && snoozedCommandId === commandId;

/**
 * Mounts the restore interlock above the router so it survives navigation
 * and covers whichever page the operator happens to be on. Renders nothing
 * at all until the box actually has a restore to talk about.
 */
export function RestoreConfirmationMount() {
  const { data, isSubmitting, submitError, submit } = useRestoreState();
  const { push } = useRouter();

  // Deliberately not persisted: the restore is still pending, and once
  // nothing is in the way the operator has to answer it.
  const [snoozedCommandId, setSnoozedCommandId] = useState<string>();

  const isBusy = data?.robotActivity.isBusy ?? false;
  const isAwaiting = data?.state === 'awaitingConfirmation';

  // The two states the operator may put aside: the robot is busy and they
  // need to go stop it, or they have answered and updater-bot has not picked
  // it up — which never resolves on its own if updater-bot is dead.
  const canSnooze = isBusy || data?.state === 'confirmed';

  const isSnoozed = isSnoozedFor(snoozedCommandId, data?.commandId);

  const isCloudBackupEnabled = useFeatureFlag('cbMigration');

  // Only while the offer is on screen: this polls, and a box with no restore
  // pending has nothing to ask about.
  const { isCloudConnected, isChecking } = useIsCloudConnected({
    enabled: isCloudBackupEnabled && isAwaiting,
  });

  // Owned here rather than in the modal on purpose. A complete backup runs
  // for minutes, and the modal unmounts the moment the operator snoozes it
  // or answers — which would drop the capture's result, and its toast, on
  // the floor.
  const cloudBackup = useCreateCloudBackup();

  if (isSnoozed && !canSnooze) {
    setSnoozedCommandId(undefined);
  }

  // Once the restore has applied, the token this page holds authenticates
  // against a database that no longer exists. This throws it away and
  // reloads; the completion modal comes back after the operator logs in,
  // because the command is still unacknowledged on disk.
  useDropSessionAfterRestore(data?.state, data?.commandId, data?.finishedAt);

  if (!data || data.state === 'idle' || (isSnoozed && canSnooze)) {
    return null;
  }

  return (
    <RestoreConfirmationModal
      data={data}
      isSubmitting={isSubmitting}
      submitError={submitError}
      onSnooze={() => setSnoozedCommandId(data.commandId)}
      backup={{
        isAvailable: isCloudBackupEnabled,
        isCloudConnected,
        isCheckingConnection: isChecking,
        isRunning: cloudBackup.isPending,
        hasSucceeded:
          cloudBackup.isSuccess &&
          cloudBackup.data.status !== 'failed' &&
          !cloudBackup.data.error &&
          !cloudBackup.data.params?.registrationError,
        onStart: () => cloudBackup.mutate({}),
      }}
      onOpenSection={(url) => {
        // The restore is over and the operator has already logged back in,
        // so this is an ordinary acknowledge-and-navigate.
        if (data.commandId) {
          submit({
            commandId: data.commandId,
            decision: 'acknowledge',
          }).catch(() => {});
        }

        push(url);
      }}
      onSubmit={(input) => {
        submit(input).catch(() => {});
      }}
    />
  );
}
